import {
  ColorMatrixFilter,
  Container,
  FillGradient,
  Graphics,
  NoiseFilter,
  Rectangle,
  type Ticker,
} from 'pixi.js';
import { DESIGN_HEIGHT, DESIGN_WIDTH } from '../data/constants';

// Old-film post effect (1930s vibe): animated grain, brightness flicker, a
// static vignette and occasional scratches. Pure render - applied as filters
// on the scene container + a top overlay. Engine never sees it. Driven by wall
// time so battle pace (ticker.speed) doesn't slow it.
//   'sepia' - warm brown tone (reads as old B&W).
//   'color' - keeps the original colours, just aged contrast (faded film).
export type FilmMode = 'sepia' | 'color';

// --- tuning (all in one place; this is the "light" preset) ---
const GRAIN = 0.02; // noise intensity 0..1
const VIGNETTE_INNER = 0.45; // clear radius (fraction, larger = brighter)
const VIGNETTE_OUTER = 0.95;
const VIGNETTE_ALPHA = 0.15; // corner darkness 0..1
const CONTRAST_SEPIA = 0.1;
const CONTRAST_COLOR = 0.05;
const FLICKER_AMP = 0.03; // +-half this on brightness
const DIM_CHANCE = 0.012; // chance per frame of a slight dim flash
const DIM_FACTOR = 0.93; // how much that flash dims
const SCRATCH_MIN_MS = 240; // min gap between scratch redraws
const SCRATCH_RAND_MS = 520; // extra random gap
const SCRATCH_MAX_ALPHA = 0.08;

const VIGNETTE = new FillGradient({
  type: 'radial',
  center: { x: 0.5, y: 0.5 },
  innerRadius: VIGNETTE_INNER,
  outerCenter: { x: 0.5, y: 0.5 },
  outerRadius: VIGNETTE_OUTER,
  colorStops: [
    { offset: 0, color: 'rgba(0,0,0,0)' },
    { offset: 1, color: 'rgba(0,0,0,' + VIGNETTE_ALPHA + ')' },
  ],
  textureSpace: 'local',
});

export class FilmEffect {
  private readonly colorMatrix = new ColorMatrixFilter();
  private readonly noise = new NoiseFilter({ noise: GRAIN, seed: Math.random() });
  private readonly overlay = new Container();
  private readonly scratches = new Graphics();
  private nextScratchAt = 0;
  private update?: () => void;

  constructor(
    scene: Container,
    private readonly ticker: Ticker,
    private readonly mode: FilmMode = 'sepia',
  ) {
    scene.filters = [this.colorMatrix, this.noise];
    // Filters need a defined region; everything is authored in design space.
    scene.filterArea = new Rectangle(0, 0, DESIGN_WIDTH, DESIGN_HEIGHT);

    const vignette = new Graphics().rect(0, 0, DESIGN_WIDTH, DESIGN_HEIGHT).fill(VIGNETTE);
    this.overlay.addChild(vignette, this.scratches);
    // Topmost child of the scene so it scales with the letterbox layout.
    scene.addChild(this.overlay);
  }

  start(): void {
    this.update = () => this.tick();
    this.ticker.add(this.update);
  }

  private tick(): void {
    // Rebuild the matrix each frame: reset -> [sepia] -> contrast -> flicker.
    // Color mode skips sepia so the original colours stay.
    const cm = this.colorMatrix;
    cm.reset();
    if (this.mode === 'sepia') cm.sepia(true);
    cm.contrast(this.mode === 'sepia' ? CONTRAST_SEPIA : CONTRAST_COLOR, true);
    let brightness = 1 + (Math.random() - 0.5) * FLICKER_AMP;
    if (Math.random() < DIM_CHANCE) brightness *= DIM_FACTOR;
    cm.brightness(brightness, true);

    // New grain pattern every frame.
    this.noise.seed = Math.random();

    // Occasional vertical scratches, scheduled by wall time.
    const now = performance.now();
    if (now >= this.nextScratchAt) {
      this.drawScratches();
      this.nextScratchAt = now + SCRATCH_MIN_MS + Math.random() * SCRATCH_RAND_MS;
    }
  }

  private drawScratches(): void {
    this.scratches.clear();
    const roll = Math.random();
    // Mostly nothing, sometimes one faint line, rarely two.
    const count = roll < 0.7 ? 0 : roll < 0.95 ? 1 : 2;
    for (let i = 0; i < count; i++) {
      const x = Math.random() * DESIGN_WIDTH;
      const alpha = 0.02 + Math.random() * (SCRATCH_MAX_ALPHA - 0.02);
      this.scratches.rect(x, 0, 1, DESIGN_HEIGHT).fill({ color: 0xffffff, alpha });
    }
  }
}
